import { useCallback, useEffect, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import {
  deleteProjectMedia,
  listProjectMedia,
  uploadOriginalMedia,
  type MediaItem,
} from '@/lib/media';
import { useNoindex } from '@/hooks/useNoindex';
import { useToast } from '@/components/base/Toast';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import MediaCard from '@/pages/admin/media/components/MediaCard';
import MediaDetailModal from '@/pages/admin/media/components/MediaDetailModal';

export default function AdminMedia() {
  useNoindex();
  const { showToast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [selected, setSelected] = useState<MediaItem | null>(null);
  const [confirmItem, setConfirmItem] = useState<MediaItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listProjectMedia();
      const sorted = [...list].sort(
        (a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
      );
      setItems(sorted);
    } catch (err) {
      setError((err as Error).message || 'メディアの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleFiles = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      const files = Array.from(fileList);
      setUploading(true);
      let success = 0;
      let failed = 0;
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const result = await uploadOriginalMedia(file);
        if (result.success) success += 1;
        else failed += 1;
      }
      setUploading(false);
      if (success > 0) {
        showToast(`${success} 件のファイルをアップロードしました`, 'success');
      }
      if (failed > 0) {
        showToast(`${failed} 件のアップロードに失敗しました`, 'error');
      }
      await load();
    },
    [load, showToast]
  );

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    handleFiles(event.dataTransfer.files);
  };

  const handleDelete = async () => {
    if (!confirmItem) return;
    setDeleting(true);
    const result = await deleteProjectMedia(confirmItem.key);
    setDeleting(false);
    if (result.success) {
      showToast('ファイルを削除しました', 'success');
      setConfirmItem(null);
      setSelected(null);
      await load();
    } else {
      showToast(result.message || '削除に失敗しました。', 'error');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-foreground-500">
            アップロードしたメディアの管理を行います。
          </p>
          <p className="mt-0.5 text-xs text-foreground-400">全 {items.length} 件</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading || uploading}
            className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 disabled:opacity-60"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={loading ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} />
            </span>
            更新
          </button>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 rounded-md bg-primary-500 px-3.5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={uploading ? 'ri-loader-4-line animate-spin' : 'ri-upload-cloud-2-line'} />
            </span>
            アップロード
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors ${
          dragging
            ? 'border-primary-400 bg-primary-50'
            : 'border-background-300 bg-background-100/40'
        }`}
      >
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-200 text-foreground-600">
          <i className="ri-drag-drop-line text-lg" />
        </span>
        <p className="text-sm text-foreground-700">
          ここにファイルをドラッグ＆ドロップ、または
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="mx-1 cursor-pointer text-primary-700 underline underline-offset-2"
          >
            ファイルを選択
          </button>
        </p>
        <p className="text-xs text-foreground-400">複数ファイルの同時アップロードに対応しています</p>
      </div>

      {uploading && (
        <div className="flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-loader-4-line animate-spin" />
          アップロード中です。完了までお待ちください...
        </div>
      )}

      {error && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-800">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line" />
            {error}
          </span>
          <button
            type="button"
            onClick={load}
            className="rounded-md bg-accent-600 px-3 py-1.5 text-xs text-background-50 hover:bg-accent-700"
          >
            再試行
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex h-64 flex-col items-center justify-center gap-3 text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-2xl" />
          <span className="text-sm">読み込み中...</span>
        </div>
      ) : items.length === 0 && !error ? (
        <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-lg border border-background-200 bg-background-50 text-center text-foreground-500">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-200">
            <i className="ri-image-2-line text-lg" />
          </span>
          <p className="text-sm">メディアはまだありません。</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((item) => (
            <MediaCard
              key={item.key}
              item={item}
              onOpen={setSelected}
              onDelete={setConfirmItem}
            />
          ))}
        </div>
      )}

      <MediaDetailModal item={selected} onClose={() => setSelected(null)} onDelete={setConfirmItem} />

      <ConfirmDialog
        open={Boolean(confirmItem)}
        title="ファイルを削除しますか？"
        message={
          <>
            この操作は取り消せません。
            <br />
            <span className="font-medium text-foreground-800">{confirmItem?.fileName}</span>
          </>
        }
        confirmLabel="削除する"
        danger
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmItem(null)}
      />
    </div>
  );
}